import { defineStore } from 'pinia';
import axios from 'axios';
import { useScenariosStore } from './scenarios';
import {getAPIURL} from '../logic/api';

const initialState = {
  events: [],
  eventsByMonth: [],
  filteredEvents: [],
  combinedActiveEvents: [],
  loading: false,
  error: null,
  profile: null,
  currentDate: new Date()
};

export const useEventsStore = defineStore('events', {
  state: () => ({ ...initialState }),

  getters: {
    // Get all events
    allEvents: (state) => state.events || [],
    
    // Get events by month
    monthlyEvents: (state) => state.eventsByMonth || [],
    
    // Get filtered events
    getFilteredEvents: (state) => state.filteredEvents || [],
    
    // Get combined active events
    activeEvents: (state) => state.combinedActiveEvents || [],
    
    // Get current date
    getCurrentDate: (state) => state.currentDate,
    
    // Get loading state
    isLoading: (state) => state.loading,
    
    // Get error state
    hasError: (state) => state.error !== null,
    
    // Get selected scenario from scenarios store
    selectedScenario() {
      const scenariosStore = useScenariosStore();
      return scenariosStore.selectedScenario;
    },
    
    // Get scenarios from scenarios store
    scenarios() {
      const scenariosStore = useScenariosStore();
      return scenariosStore.allScenarios;
    },
    
    // Calculate cash flow totals
    cashFlowInTotal: (state) => {
      let totalCredit = 0;
      if (state.events && state.events.length > 0) {
        for (let event of state.events) {
          if (event.type === 'CREDIT') {
            totalCredit += parseFloat(event.amount);
          }
        }
      }
      return totalCredit;
    },
    
    cashFlowOutTotal: (state) => {
      let totalDebit = 0;
      if (state.events && state.events.length > 0) {
        for (let event of state.events) {
          if (event.type === 'DEBIT') {
            totalDebit += parseFloat(event.amount);
          }
        }
      }
      return totalDebit;
    },
    
    // Get events for current month
    eventsForMonth: (state) => {
      if (!state.events || !state.events.length) {
        return [];
      }
      const year = state.currentDate.getFullYear();
      const month = state.currentDate.getMonth();
      return state.events.filter(event => {
        const eventDate = new Date(event.start_date);
        eventDate.setHours(0, 0, 0, 0);
        return eventDate.getTime() === state.currentDate.getTime();
      });
    }
  },

  actions: {
    // Set the profile for this store
    setProfile(profile) {
      if (profile && profile.id) {
        this.$patch((state) => {
          state.profile = profile;
        });
        // console.log('Profile set in events store:', profile);
      } else {
        console.warn('Invalid profile provided to events store:', profile);
      }
    },

    // Set current date
    setCurrentDate(date) {
      if (date instanceof Date) {
        this.$patch((state) => {
          state.currentDate = date;
        });
        // console.log('Current date set in events store:', date);
      } else {
        console.warn('Invalid date provided to events store:', date);
        // Set to current date as fallback
        this.$patch((state) => {
          state.currentDate = new Date();
        });
      }
    },

    // Fetch events for the selected scenario
    async fetchEvents() {
      if (!this.selectedScenario?.id || !this.profile?.id) {
        console.error('No scenario or profile set for fetching events');
        return;
      }

      this.$patch((state) => {
        state.loading = true;
        state.error = null;
      });

      try {
        // console.log('fetchEvents called with scenarioID:', this.selectedScenario.id);
        const url = getAPIURL()+'/api/scenario/get-events-for-scenario';
        const response = await axios.get(url, {
          params: {
            scenarioID: this.selectedScenario.id,
            profileID: this.profile.id
          }
        });
        this.$patch((state) => {
          state.events = response.data;
        });
        // console.log('Fetched events:', response.data);
      } catch (error) {
        console.error('Error fetching events:', error);
        this.$patch((state) => {
          state.error = error.message || 'Failed to fetch events';
        });
      } finally {
        this.$patch((state) => {
          state.loading = false;
        });
      }
    },

    // Fetch events for a specific month by scenario
    async fetchEventsForMonthByScenario() {
      if (!this.selectedScenario?.id || !this.profile?.id) {
        console.error('No scenario or profile set for fetching monthly events');
        return;
      }

      this.$patch((state) => {
        state.loading = true;
        state.error = null;
      });

      // Ensure currentDate is set, default to current date if not
      if (!this.currentDate) {
        this.setCurrentDate(new Date());
      }

      try {
        const month = Number(this.currentDate.getMonth());
        const year = Number(this.currentDate.getFullYear());
        // console.log('fetchEventsForMonthByScenario called with scenarioID:', this.selectedScenario.id);
        // console.log('Month:', month, 'Year:', year);
        
        const url = getAPIURL()+'/api/scenario/get-events-for-scenario-for-month';
        const response = await axios.get(url, {
          params: {
            scenarioID: this.selectedScenario.id,
            profileID: this.profile.id,
            month: month,
            year: year
          }
        });
        this.$patch((state) => {
          state.eventsByMonth = response.data;
        });
        // console.log('Fetched eventsByMonth:', response.data);
        
        // Convert the monthly events to filtered events format
        this.convertMonthlyEventsToFiltered();
      } catch (error) {
        console.error('Error fetching events for month by scenario:', error);
        this.$patch((state) => {
          state.error = error.message || 'Failed to fetch monthly events';
        });
      } finally {
        this.$patch((state) => {
          state.loading = false;
        });
      }
    },

    // Fetch events for a specific date range
    async fetchEventsForDateRange(startDate, endDate) {
      if (!this.profile?.id) {
        console.error('No profile set for fetching events for date range');
        return;
      }

      this.$patch((state) => {
        state.loading = true;
        state.error = null;
      });

      try {
        // Calculate all months in the date range
        const monthsToFetch = [];
        const startYear = startDate.getUTCFullYear();
        const startMonth = startDate.getUTCMonth();
        const endYear = endDate.getUTCFullYear();
        const endMonth = endDate.getUTCMonth();
        
        let currentYear = startYear;
        let currentMonth = startMonth;
        
        while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
          monthsToFetch.push({ year: currentYear, month: currentMonth });
          
          currentMonth++;
          if (currentMonth > 11) {
            currentMonth = 0;
            currentYear++;
          }
        }
        
        // Fetch data for all active scenarios and all months in the range
        const allEvents = [];
        const scenariosStore = useScenariosStore();
        
        // For now, we'll fetch for the selected scenario only
        // This can be expanded to fetch for multiple active scenarios
        for (const { year, month } of monthsToFetch) {
          const response = await axios.get(getAPIURL()+'/api/scenario/get-events-for-scenario-for-month', {
            params: {
              scenarioID: this.selectedScenario?.id,
              profileID: this.profile.id,
              month: month,
              year: year
            }
          });
          
          allEvents.push(...response.data);
        }
        
        // Filter the events based on the date range
        this.filterEventsByDateRange(startDate, endDate, allEvents);
      } catch (error) {
        console.error('Error fetching events for date range:', error);
        this.$patch((state) => {
          state.error = error.message || 'Failed to fetch events for date range';
        });
      } finally {
        this.$patch((state) => {
          state.loading = false;
        });
      }
    },

    // Convert monthly events to filtered events format with date range filtering
    convertMonthlyEventsToFiltered(startDate = null, endDate = null) {
      const filteredEvents = [];
      
      if (this.eventsByMonth && this.eventsByMonth.length > 0) {
        this.eventsByMonth.forEach(eventData => {
          if (eventData.occurrences) {
            eventData.occurrences.forEach(occurrence => {
              // Parse occurrence date properly to avoid timezone issues
              let occurrenceDate;
              if (typeof occurrence === 'string') {
                if (occurrence.includes('T')) {
                  // Handle ISO datetime strings (2025-07-01T00:00:00.000Z)
                  const datePart = occurrence.split('T')[0];
                  const [year, month, day] = datePart.split('-').map(Number);
                  occurrenceDate = new Date(Date.UTC(year, month - 1, day));
                } else {
                  // Handle date-only strings (2025-07-01)
                  const [year, month, day] = occurrence.split('-').map(Number);
                  occurrenceDate = new Date(Date.UTC(year, month - 1, day));
                }
              } else {
                occurrenceDate = new Date(occurrence);
              }
              
              // If date range is specified, filter by it
              if (startDate && endDate) {
                const occurrenceDateString = occurrenceDate.toISOString().split('T')[0];
                const startDateString = startDate.toISOString().split('T')[0];
                const endDateString = endDate.toISOString().split('T')[0];
                
                if (occurrenceDateString < startDateString || occurrenceDateString > endDateString) {
                  return; // Skip events outside the date range
                }
              }
              
              const eventToAdd = {
                ...eventData.event,
                date: occurrenceDate.toISOString().split('T')[0]
              };
              filteredEvents.push(eventToAdd);
            });
          }
        });
      }
      
      // Update the filtered events using $patch to avoid proxy issues
      this.$patch((state) => {
        state.filteredEvents = filteredEvents;
      });
    },

    // Filter events by date range
    filterEventsByDateRange(startDate, endDate, allEvents) {
      // Filter events based on their occurrences within the date range
      const filteredEvents = [];
      const seenEvents = new Set(); // Track unique event-date combinations
      
      if (allEvents && allEvents.length > 0) {
        allEvents.forEach(eventData => {
          if (eventData.occurrences) {
            eventData.occurrences.forEach(occurrence => {
              // Parse occurrence date properly to avoid timezone issues
              let occurrenceDate;
              if (typeof occurrence === 'string') {
                if (occurrence.includes('T')) {
                  // Handle ISO datetime strings (2025-07-01T00:00:00.000Z)
                  const datePart = occurrence.split('T')[0];
                  const [year, month, day] = datePart.split('-').map(Number);
                  occurrenceDate = new Date(Date.UTC(year, month - 1, day));
                } else {
                  // Handle date-only strings (2025-07-01)
                  const [year, month, day] = occurrence.split('-').map(Number);
                  occurrenceDate = new Date(Date.UTC(year, month - 1, day));
                }
              } else {
                occurrenceDate = new Date(occurrence);
              }
              
              // Compare dates by their date strings to avoid time component issues
              const occurrenceDateString = occurrenceDate.toISOString().split('T')[0];
              const startDateString = startDate.toISOString().split('T')[0];
              const endDateString = endDate.toISOString().split('T')[0];
              
              if (occurrenceDateString >= startDateString && occurrenceDateString <= endDateString) {
                // Create a unique key for this event-date combination to prevent duplicates
                const eventKey = `${eventData.event.id || eventData.event._id}-${occurrenceDateString}`;
                
                if (!seenEvents.has(eventKey)) {
                  seenEvents.add(eventKey);
                  const eventToAdd = {
                    ...eventData.event,
                    date: occurrenceDateString
                  };
                  filteredEvents.push(eventToAdd);
                }
              }
            });
          }
        });
      }
      
      // Update the filtered events using $patch to avoid proxy issues
      this.$patch((state) => {
        state.filteredEvents = filteredEvents;
      });
    },

    // Get all active scenario events (for dashboard)
    async getAllActiveScenarioEvents(activeScenarios) {
      const allEvents = [];

      if (!this.profile?.id) {
        return allEvents;
      }

      const profileID = this.profile.id;
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();

      try {
        // Get events for each active scenario
        for (const scenarioId of activeScenarios) {
          // console.log('Fetching events for scenario:', scenarioId);
          let payload = {
            params: {
              scenarioID:
                scenarioId === "default"
                  ? this.selectedScenario?.id
                  : scenarioId,
              profileID: profileID,
              month: currentMonth,
              year: currentYear,
            },
          };
          const response = await axios.get(
            "https://budgeteer.x01r.xyz/api/scenario/get-events-for-scenario-for-month",
            payload
          );
          // console.log('Response for scenario', scenarioId, ':', response.data);

          if (response.data && response.data.length > 0) {
            response.data.forEach((eventData) => {
              if (eventData.occurrences) {
                eventData.occurrences.forEach((occurrence) => {
                  const eventToAdd = {
                    ...eventData.event,
                    date: occurrence,
                  };
                  allEvents.push(eventToAdd);
                });
              }
            });
          }
        }
      } catch (error) {
        console.error("Error fetching events for active scenarios:", error);
        this.$patch((state) => {
          state.error = error.message || 'Failed to fetch active scenario events';
        });
      }
      
      // console.log('All events for active scenarios:', allEvents);
      // Update combined active events using $patch to avoid proxy issues
      this.$patch((state) => {
        state.combinedActiveEvents = allEvents;
      });
      return allEvents;
    },

    // Create a new event
    async createEvent(eventData) {
      if (!this.profile?.id) {
        throw new Error('No profile set for creating event');
      }

      this.$patch((state) => {
        state.loading = true;
        state.error = null;
      });

      try {
        // console.log('Creating event:', eventData);
        
        const url = getAPIURL()+'/api/scenario/create-event';
        const response = await axios.post(url, {
          ...eventData,
          profileID: this.profile.id
        });
        
        // console.log('Created event:', response.data);
        
        // Refresh events after creation
        await this.fetchEvents();
        await this.fetchEventsForMonthByScenario();
        
        return response.data;
      } catch (error) {
        console.error('Error creating event:', error);
        this.$patch((state) => {
          state.error = error.message || 'Failed to create event';
        });
        throw error;
      } finally {
        this.$patch((state) => {
          state.loading = false;
        });
      }
    },

    // Update an event
    async updateEvent(eventId, updates) {
      if (!this.profile?.id) {
        throw new Error('No profile set for updating event');
      }

      this.$patch((state) => {
        state.loading = true;
        state.error = null;
      });

      try {
        // console.log('Updating event:', eventId, updates);
        
        const url = getAPIURL()+'/api/scenario/update-event';
        const response = await axios.put(url, {
          eventID: eventId,
          profileID: this.profile.id,
          ...updates
        });
        
        // console.log('Updated event:', response.data);
        
        // Refresh events after update
        await this.fetchEvents();
        await this.fetchEventsForMonthByScenario();
        
        return response.data;
      } catch (error) {
        console.error('Error updating event:', error);
        this.$patch((state) => {
          state.error = error.message || 'Failed to update event';
        });
        throw error;
      } finally {
        this.$patch((state) => {
          state.loading = false;
        });
      }
    },

    // Delete an event
    async deleteEvent(eventId) {
      if (!this.profile?.id) {
        throw new Error('No profile set for deleting event');
      }

      this.$patch((state) => {
        state.loading = true;
        state.error = null;
      });

      try {
        // console.log('Deleting event:', eventId);
        
        const url = getAPIURL()+'/api/scenario/remove-event';
        await axios.delete(url, {
          data: {
            eventID: eventId,
            profileID: this.profile.id
          }
        });
        
        // console.log('Event deleted successfully');
        
        // Refresh events after deletion
        await this.fetchEvents();
        await this.fetchEventsForMonthByScenario();
      } catch (error) {
        console.error('Error deleting event:', error);
        this.$patch((state) => {
          state.error = error.message || 'Failed to delete event';
        });
        throw error;
      } finally {
        this.$patch((state) => {
          state.loading = false;
        });
      }
    },

    // Fetch a specific event by ID
    async fetchEventById(eventId) {
      if (!this.profile?.id || !eventId) {
        console.error('No profile or event ID set for fetching event');
        return null;
      }

      this.$patch((state) => {
        state.loading = true;
        state.error = null;
      });

      try {
        // Use the existing endpoint to get all events for the scenario, then filter for the specific event
        const url = getAPIURL()+'/api/scenario/get-events-for-scenario';
        const response = await axios.get(url, {
          params: {
            scenarioID: this.selectedScenario?.id,
            profileID: this.profile.id
          }
        });
        
        // Find the specific event by ID
        const event = response.data.find(e => e.id === eventId || e._id === eventId);
        
        if (event) {
          return event;
        } else {
          console.error('Event not found with ID:', eventId);
          return null;
        }
      } catch (error) {
        console.error('Error fetching event by ID:', error);
        this.$patch((state) => {
          state.error = error.message || 'Failed to fetch event by ID';
        });
        return null;
      } finally {
        this.$patch((state) => {
          state.loading = false;
        });
      }
    },

    // Reset the store to initial state
    reset() {
      this.$patch((state) => {
        Object.assign(state, initialState);
      });
    },

    // Reset for new user
    resetForNewUser() {
      this.reset();
      this.$patch((state) => {
        state.profile = null;
      });
    },

    // Clear error
    clearError() {
      this.$patch((state) => {
        state.error = null;
      });
    },

    // Set filtered events (for scenario combination)
    setFilteredEvents(events) {
      this.$patch((state) => {
        state.filteredEvents = events || [];
      });
    }
  }
});
